import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ZoomInIcon, ZoomOutIcon } from '@tink/core/src/components/atoms/Icons/Zoom';
import { SpeedDecreaseIcon, SpeedIncreaseIcon } from '@tink/core/src/components/atoms/Icons/Speed';
import { RotateIcon } from '@tink/core/src/components/atoms/Icons/Rotate';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { Caption } from '../../../../atoms/Text/Text';
import { SolidContainer } from '../../../../atoms/Container/Container';
import { useChipTanFlicker } from './useChipTanFlicker';
import { messages } from './messages';
import { StyledBox, StyledButton, StyledChipTanBox } from './ChipTanFlicker.styles';

type ChipTanProps = {
  code: string;
};

export const ChipTanFlicker = ({ code }: ChipTanProps) => {
  const { formatMessage } = useIntl();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scaleUp, scaleDown, speedUp, slowDown, rotate, startFlicker, stopFlicker } =
    useChipTanFlicker({
      code,
      canvasRef,
      containerRef,
    });

  useEffect(() => {
    startFlicker();

    return () => {
      stopFlicker();
    };
  }, [startFlicker, stopFlicker]);

  return (
    <div data-testid="chip-tan-flicker">
      <SolidContainer display="flex" style={{ userSelect: 'none' }} px="normal" py="xxsmall">
        <StyledBox>
          <Caption mb="none" mr="xxsmall" color="secondary">
            <FormattedMessage {...messages.size} />
          </Caption>
          <StyledButton onClick={scaleDown} mr="xxxsmall" aria-label="Scale down">
            <ZoomOutIcon />
          </StyledButton>
          <StyledButton onClick={scaleUp} aria-label="Scale up">
            <ZoomInIcon />
          </StyledButton>
        </StyledBox>
        <StyledBox style={{ flex: 1 }}>
          <Caption mb="none" mr="xxsmall" color="secondary">
            <FormattedMessage {...messages.speed} />
          </Caption>
          <StyledButton onClick={slowDown} mr="xxxsmall" aria-label="Slow down">
            <SpeedDecreaseIcon />
          </StyledButton>
          <StyledButton onClick={speedUp} aria-label="Speed up">
            <SpeedIncreaseIcon />
          </StyledButton>
        </StyledBox>
        <StyledButton aria-label={formatMessage(messages.rotate)} onClick={rotate}>
          <Box mr="xxxsmall">
            <RotateIcon />
          </Box>
          <Caption mb="none" color="primary">
            <FormattedMessage {...messages.rotate} />
          </Caption>
        </StyledButton>
      </SolidContainer>
      <StyledChipTanBox my="medium" ref={containerRef}>
        <canvas aria-label="chiptan" ref={canvasRef} />
      </StyledChipTanBox>
    </div>
  );
};
